import { computed } from 'mobx'
import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { type ConversationViewModel } from '@/features/messages/ConversationViewModel'
import type { SubmittedJob } from './SubmittedJob'
import { ChatListViewModel } from '@/features/messages/ChatListViewModel'

/**
 * Minimal interface for a job-related conversation view model.
 * Each job type may have its own implementation of this to facilitate
 * wrapping messages to handle special message content types.
 * For example, tax jobs have external-reference messages for job progression
 * updates.
 */
export interface JobConversationViewModel extends ActivationLifecycle {
  /**
   * The unread message count.
   */
  readonly unreadCount: number

  /**
   * Underlying conversation view model.
   */
  readonly conversationVm: ConversationViewModel

  /**
   * Whether the chat is available during info gathering.
   */
  readonly isAvailableInInfoGathering: boolean

  /**
   * Fetches the unread count.
   */
  fetchUnreadCount(signal?: AbortSignal): Promise<void>
}

/**
 * Default implementation of {@link JobConversationViewModel}.
 */
export class DefaultJobConversationViewModel implements JobConversationViewModel {
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * The chat list view model.
   */
  readonly chatVm

  constructor(
    public readonly job: SubmittedJob,
    public readonly conversationVm: ConversationViewModel,
  ) {
    this.chatVm = new ChatListViewModel({
      messages: () => this.conversationVm.messages.slice(),
      getMessageSender: (vm) => vm.message.sender,
      getMessageDate: (vm) => vm.message.dateCreated,
      maxDurationBetweenSameSenderMessages: 3 * 60 * 1000, // 3 minutes
    })
    this._lifecycle.manage(this.chatVm)
    this._lifecycle.manage(this.conversationVm)
  }

  /**
   * The number of unread messages in the conversation.
   */
  @computed get unreadCount() {
    return this.conversationVm.unreadCount
  }

  /** @inheritDoc */
  @computed get isAvailableInInfoGathering() {
    return this.job.workspace.config.jobs.enableChatInInfoGathering
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
    void this.conversationVm.fetchMessages(this._lifecycle.signal)
  }

  /**
   * Fetches the unread count.
   */
  async fetchUnreadCount(signal?: AbortSignal) {
    // The conversation will contain the unread count.
    await this.conversationVm.fetchConversation(signal)
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }
}
